<template>
  <spinner-container :loading="localLoading">
    <bread-cump :cumps="$route.meta.breadcrumps" />

    <v-container fluid>
      <v-row>
        <v-col lg="12">
          <v-card class="card--shadow">
            <v-card-text>
              <patient-info :patient="patient" />
              <v-form ref="form" v-model="isValid" lazy-validation>
                <v-row>
                  <v-col cols="12" md="8">
                    <v-select
                      :items="complementarySupportTypes"
                      v-model="form.support_ids"
                      item-text="support_name"
                      item-value="id" 
                      label="Support Types"
                      :rules="[v => (v && v.length > 0) || 'At least one support type is required']"
                      dense
                      outlined
                      clearable
                      multiple
                      chips
                      class="mb-4"
                    />
                    <v-row class="mt-4">
                      <v-col cols="12" sm="6" md="6">
                        <v-select
                          :items="months"
                          v-model="form.support_month"
                          label="Support Month"
                          item-text="text"
                          item-value="value"
                          dense
                          outlined
                          clearable
                          :rules="[v => !!v || 'Support month is required']"
                          @change="updateDateFromMonthYear"
                        />
                      </v-col>
                      <v-col cols="12" sm="6" md="6">
                        <v-select
                          :items="years"
                          v-model="form.support_year"
                          label="Support Year"
                          item-text="text"
                          item-value="value"
                          dense
                          outlined
                          clearable
                          :rules="[v => !!v || 'Support year is required']"
                          @change="updateDateFromMonthYear"
                        />
                      </v-col>
                    </v-row>
                    <v-menu
                      ref="dateMenu"
                      v-model="dateMenu"
                      :close-on-content-click="false"
                      transition="scale-transition"
                      offset-y
                      min-width="auto"
                    >
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          v-model="form.date"
                          label="Date"
                          readonly
                          outlined
                          dense
                          v-bind="attrs"
                          v-on="on"
                          :rules="[v => !!v || 'Date is required']"
                        />
                      </template>
                      <v-date-picker v-model="form.date" @input="dateMenu = false" />
                    </v-menu>
                    <v-textarea
                      v-model="form.remark"
                      label="Remark"
                      rows="3"
                      outlined
                      auto-grow
                      class="mt-4"
                    />
                  </v-col>
                  <v-col cols="12" md="4">
                    <div class="d-flex align-center mb-4">
                      <v-checkbox
                        v-model="form.reimbursement"
                        label="Reimbursement"
                        hide-details
                      />
                    </div>
                  </v-col>
                </v-row>

                <div class="d-flex flex-wrap">
                  <v-btn class="mr-4 mb-2 form-btn" @click="submit" :loading="loading">
                    <v-icon left>mdi-chevron-right-circle-outline</v-icon>Submit
                  </v-btn>
                  <v-btn outlined color="grey" depressed class="mb-2" @click="cancel">
                    Cancel
                  </v-btn>
                </div>
              </v-form>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
      
      <v-row v-show="patient.treatment_startdate != null">
        <v-col lg="12">
          <v-card class="card--shadow">
            <v-card-title>
              <span class="headline">Complementary Support List</span>
            </v-card-title>
            <v-card-text>
              <v-data-table
                :headers="tbHeaders"
                :items="dataHandler"
                :loading="loading"
                :items-per-page="itemsPerPage"
                @page-count="pageCount = $event"
                :page.sync="page"
                item-key="id"
                hide-default-footer
                hide-default-header
              >
                <template v-slot:header="{ props: { headers } }">
                  <thead class="header">
                    <tr>
                      <th
                        v-for="header in headers"
                        :key="header.text"
                        class="header--text"
                      >
                        <span v-if="header.value">{{ header.text }}</span>
                      </th>
                    </tr>
                  </thead>
                </template>

                <template v-slot:item="{ index, item }">
                  <tr>
                    <td>{{ index + 1 }}</td>
                    <td>{{ item.support_name }}</td>
                    <td>{{ item.support_month_text }}</td>
                    <td>{{ item.support_year_text }}</td>
                    <td>{{ item.date }}</td>
                    <td>{{ item.reimbursement == 0 ? 'No' : 'Yes' }}</td>
                    <td>{{ item.remark || '-' }}</td>
                    <td>
                      <v-btn
                        @click="deleteSupport(item)"
                        icon
                        color="red lighten-1"
                        class="white--text"
                        depressed
                        small
                      >
                        <v-icon>mdi-delete-outline</v-icon>
                      </v-btn>
                    </td>
                  </tr>
                </template>
              </v-data-table>
              <div class="text-center py-3" v-if="pageCount > 1">
                <v-pagination
                  color="purple accent-4"
                  v-model="page"
                  :length="pageCount"
                ></v-pagination>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </spinner-container>
</template>

<script>
import SpinnerContainer from "@/components/container/SpinnerContainer";
import BreadCump from "@/components/navigation/BreadCump";
import PatientInfo from "./component/patientInfo.vue";
import { mapState } from "vuex";

export default {
  components: {
    BreadCump,
    SpinnerContainer,
    PatientInfo,
  },
  data: () => ({
    isValid: true,
    dateMenu: false,
    pageCount: 0,
    page: 1,
    itemsPerPage: 10,
    tbHeaders: [
      { text: 'No', value: 'no', sortable: false },
      { text: 'Support Type', value: 'support_name', sortable: false },
      { text: 'Support Month', value: 'support_month_text', sortable: false },
      { text: 'Support Year', value: 'support_year_text', sortable: false },
      { text: 'Date', value: 'date', sortable: false },
      { text: 'Reimbursement', value: 'reimbursement', sortable: false },
      { text: 'Remark', value: 'remark', sortable: false },
      { text: 'Actions', value: 'actions', sortable: false },
    ],
    form: {
      support_ids: [],
      reimbursement: false,
      date: null,
      support_month: 0,
      support_year: 0,
      remark: '',
      patient_id: null,
      township_id: null,
    },
  }),
  computed: {
    ...mapState("InitialData", ["months", "years", "supportTypes"]),
    ...mapState("Patient", ["patient", "complementarySupports", "complementarySupportTypes"]),
    ...mapState("Loading", ["loading", "localLoading"]),
    dataHandler() {
      const supportData = this.complementarySupports.map((item) => {
        // Mapping month id
        const months = this.months.find((month) => {
          return item.support_month == month.value;
        });
        item.support_month_text = months ? months.text : '';

        // Mapping year id
        const years = this.years.find((year) => {
          return item.support_year == year.text;
        });
        item.support_year_text = years ? years.text : '';

        // Mapping support type name
        const supportType = this.supportTypes.find((support) => {
          return item.support_id == support.id;
        });
        item.support_name = supportType ? supportType.name : '';

        return item;
      });
      return supportData;
    },
  },
  methods: {
    updateDateFromMonthYear() {
      // Auto-set date to 1st of selected month and year
      if (this.form.support_month && this.form.support_year) {
        // Create date string in YYYY-MM-DD format
        const year = this.form.support_year;
        const month = this.form.support_month.toString().padStart(2, '0');
        const dateString = `${year}-${month}-01`;
        this.form.date = dateString;
      }
    },
    async submit() {
      if (this.$refs.form && !this.$refs.form.validate()) return; 
      
      this.form.patient_id = this.$route.params.id;
      this.form.township_id = this.$route.params.township_id;
      
      // Create multiple entries for each selected support type
      const promises = this.form.support_ids.map(supportId => {
        const formData = {
          ...this.form,
          support_id: supportId
        };
        return this.$store.dispatch("Patient/complementaryPatient", formData);
      });
      
      Promise.all(promises)
      .then(() => {
        this.form = {
          support_ids: [],
          reimbursement: false,
          date: null,
          support_month: 0,
          support_year: 0,
          remark: '',
          patient_id: this.$route.params.id,
          township_id: this.$route.params.township_id,
        };
        if (this.$refs.form) {
          this.$refs.form.resetValidation();
        }
        this.fetchComplementarySupports();
        this.$toast.success("Success!", { timeout: 2000 });
      })
      .catch(() => {
        this.$toast.error("Failed. Please try again.", { timeout: 2000 });
      });
    },
    cancel() {
      this.$router.push({
        name: "patient",
        params: { township_id: this.$route.params.township_id }
      });
    },
    async fetchComplementarySupports() {
      try {
        await this.$store.dispatch('Patient/getComplementarySupports', {
          patientId: this.$route.params.id,
          townshipId: this.$route.params.township_id
        });
      } catch (error) {
        console.error('Error fetching complementary supports:', error);
      }
    },
    async fetchSupportTypes() {
      try {
        await this.$store.dispatch('Patient/getComplementarySupportTypes');
        console.log('Fetched complementary support types:', this.complementarySupportTypes);
      } catch (error) {
        console.error('Error fetching complementary support types:', error);
      }
    },
    formatDate(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleDateString();
    },
    async deleteSupport(item) {
      if (confirm('Are you sure you want to delete this complementary support?')) {
        try {
          await this.$store.dispatch('Patient/deleteComplementarySupport', {
            supportId: item.id,
            townshipId: this.$route.params.township_id
          });
          this.$toast.success('Complementary support deleted successfully!', { timeout: 2000 });
          // Refresh the support list
          this.fetchComplementarySupports();
        } catch (error) {
          console.error('Error deleting complementary support:', error);
          this.$toast.error('Failed to delete complementary support. Please try again.', { timeout: 2000 });
        }
      }
    },
  },
  created() {
    this.$store.dispatch("Loading/changeLocalLoading");
    
    this.$store
      .dispatch("Patient/getPatient", this.$route.params.id)
      .then((isPass) => {
        if (isPass) {
          this.fetchComplementarySupports().then(() => {
            this.$store.dispatch("Loading/changeLocalLoading");
          });
          this.fetchSupportTypes();
        }
      });
  },
};
</script>

<style scoped>
.card--shadow {
  box-shadow: var(--primary-box-shadow) !important;
}
</style>
